import { formatMoney, type FormatMoneyOptions, type Money } from '@fd/money';
import { en } from './en.ts';
import { pl } from './pl.ts';
import { pluralCategory, selectPlural, type PluralForms } from './plural.ts';
import type { DictionaryShape, MessageKey, PluralKey } from './types.ts';

export { en } from './en.ts';
export { pl } from './pl.ts';
export { pluralCategory, selectPlural, type PluralCategory, type PluralForms } from './plural.ts';
export type { DictionaryShape, MessageKey, PluralKey } from './types.ts';

export type Language = 'pl' | 'en';
export type Dictionary = DictionaryShape<typeof en>;
export type TKey = MessageKey<typeof en>;
export type TPluralKey = PluralKey<typeof en>;
export type Vars = Readonly<Record<string, string | number>>;

export const LANGUAGES: readonly Language[] = ['pl', 'en'];

const dictionaries: Readonly<Record<Language, Dictionary>> = { en, pl };

/** Intl locale for a UI language. English defaults to en-GB (UK market), never en-US. */
export const localeFor = (language: Language): string => (language === 'pl' ? 'pl-PL' : 'en-GB');

/** Picks the UI language from the device's preferred languages. */
export function detectLanguage(preferred: readonly string[] = []): Language {
  for (const tag of preferred) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('pl')) return 'pl';
    if (lower.startsWith('en')) return 'en';
  }
  return 'en';
}

function lookup(dictionary: Dictionary, key: string): unknown {
  let node: unknown = dictionary;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.hasOwn(vars, name) ? String(vars[name]) : match,
  );
}

export type DateStyle = 'short' | 'medium' | 'long' | 'dayMonth' | 'monthYear' | 'month' | 'weekday' | 'weekdayLong' | 'dayHeading';

const DATE_OPTIONS: Readonly<Record<DateStyle, Intl.DateTimeFormatOptions>> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  dayMonth: { day: 'numeric', month: 'short' },
  monthYear: { month: 'long', year: 'numeric' },
  month: { month: 'short' },
  weekday: { weekday: 'short' },
  weekdayLong: { weekday: 'long' },
  dayHeading: { weekday: 'long', day: 'numeric', month: 'long' },
};

export type Translator = {
  readonly language: Language;
  readonly locale: string;
  t(key: TKey, vars?: Vars): string;
  plural(key: TPluralKey, count: number, vars?: Vars): string;
  /** Translation for a dynamic key built at runtime (enum values); falls back to the key. */
  dynamic(key: string, vars?: Vars): string;
  money(value: Money, options?: FormatMoneyOptions): string;
  /** A fraction as a percentage, 0.456 → "45,6%" (`digits` after the point, 1 by default; `signed`: "+3%"). */
  percent(fraction: number, options?: { digits?: number; signed?: boolean }): string;
  /** Formats a calendar date "YYYY-MM-DD" without time-zone shifts. */
  date(isoDate: string, style?: DateStyle): string;
  /** Formats a UTC timestamp in the device time zone. */
  dateTime(timestamp: string | Date): string;
  /** "8 s ago" / "8 sek. temu". */
  relative(timestamp: string | Date, now?: Date): string;
  /** A moment by the clock, in the device time zone: "dziś 09:41", "jutro 03:41", "29 wrz, 09:41". */
  when(timestamp: string | Date, now?: Date): string;
  /** Name of a system category from its stable key. */
  category(key: string): string;
};

export function createTranslator(language: Language): Translator {
  const dictionary = dictionaries[language];
  const locale = localeFor(language);
  const dateFormats = new Map<DateStyle, Intl.DateTimeFormat>();
  const percentFormats = new Map<string, Intl.NumberFormat>();
  const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });

  const dynamic = (key: string, vars?: Vars): string => {
    const value = lookup(dictionary, key);
    return typeof value === 'string' ? interpolate(value, vars) : key;
  };

  return {
    language,
    locale,
    t: (key, vars) => dynamic(key, vars),
    dynamic,
    plural(key, count, vars) {
      const forms = lookup(dictionary, key) as PluralForms | undefined;
      if (!forms || typeof forms !== 'object') return key;
      const template = selectPlural(forms, pluralCategory(language, count));
      return interpolate(template, { count, ...vars });
    },
    money: (value, options) => formatMoney(value, locale, options),
    percent(fraction, { digits = 1, signed = false } = {}) {
      const key = `${digits}|${signed}`;
      let format = percentFormats.get(key);
      if (!format) {
        format = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: digits, ...(signed ? { signDisplay: 'exceptZero' } : {}) });
        percentFormats.set(key, format);
      }
      return format.format(fraction);
    },
    date(isoDate, style = 'medium') {
      let format = dateFormats.get(style);
      if (!format) {
        format = new Intl.DateTimeFormat(locale, { ...DATE_OPTIONS[style], timeZone: 'UTC' });
        dateFormats.set(style, format);
      }
      const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
      return format.format(new Date(Date.UTC(y, m - 1, d)));
    },
    dateTime(timestamp) {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
    },
    relative(timestamp, now = new Date()) {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
      const abs = Math.abs(seconds);
      if (abs < 60) return relativeFormat.format(seconds, 'second');
      if (abs < 3600) return relativeFormat.format(Math.round(seconds / 60), 'minute');
      if (abs < 86400) return relativeFormat.format(Math.round(seconds / 3600), 'hour');
      return relativeFormat.format(Math.round(seconds / 86400), 'day');
    },
    when(timestamp, now = new Date()) {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
      const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const days = Math.round((midnight(date) - midnight(now)) / 86_400_000);
      const near = days === 0 ? 'common.todayAt' : days === 1 ? 'common.tomorrowAt' : days === -1 ? 'common.yesterdayAt' : null;
      if (near) return dynamic(near, { time });
      return `${new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date)}, ${time}`;
    },
    category(key) {
      const value = lookup(dictionary, `categories.${key}`);
      return typeof value === 'string' ? value : key;
    },
  };
}

/** Every message path in a dictionary (for completeness tests). */
export function messagePaths(dictionary: unknown, prefix = ''): string[] {
  if (typeof dictionary === 'string') return [prefix];
  if (dictionary === null || typeof dictionary !== 'object') return [];
  const record = dictionary as Record<string, unknown>;
  if (typeof record.other === 'string' && typeof record.one === 'string') return [prefix];
  return Object.entries(record).flatMap(([key, value]) =>
    messagePaths(value, prefix ? `${prefix}.${key}` : key),
  );
}
